import React, { useState } from "react";
import { motion } from "framer-motion";
import { Rocket, Sparkles, Crown, Check, Lock } from "lucide-react";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";

// Three-tier pricing card grid with a Monthly / Yearly toggle.
// Free tier ALSO lists Pro + Elite features (greyed with a lock) so users see
// what they'd unlock — but they aren't granted access.
const TIERS = [
    { id: "explorer", tierIndex: 0, icon: Rocket,   featured: false, key: "tier_explorer", ownFeatures: ["tier_explorer_f1", "tier_explorer_f2", "tier_explorer_f3"], priceKey: "tier_explorer_price", priceYearlyKey: "tier_explorer_price" },
    { id: "pro",      tierIndex: 1, icon: Sparkles, featured: true,  key: "tier_pro",      ownFeatures: ["tier_pro_f0", "tier_pro_f1", "tier_pro_f2", "tier_pro_f3"],  priceKey: "tier_pro_price", priceYearlyKey: "tier_pro_price_yearly" },
    { id: "elite",    tierIndex: 2, icon: Crown,    featured: false, key: "tier_elite",    ownFeatures: ["tier_elite_f0", "tier_elite_f1", "tier_elite_f2", "tier_elite_f3"], priceKey: "tier_elite_price", priceYearlyKey: "tier_elite_price_yearly" },
];

// Union of all feature keys across tiers, in display order.
const ALL_FEATURES = [
    ...TIERS[0].ownFeatures.map((k) => ({ k, tier: 0 })),
    ...TIERS[1].ownFeatures.map((k) => ({ k, tier: 1 })),
    ...TIERS[2].ownFeatures.map((k) => ({ k, tier: 2 })),
];

const PricingSection = () => {
    const { language } = useApp();
    const [yearly, setYearly] = useState(false);

    return (
        <section
            id="pricing"
            className="w-full py-16 md:py-24 bg-[hsl(var(--card))] border-t border-[hsl(var(--border))]"
            data-testid="landing-pricing"
        >
            <div className="max-w-6xl mx-auto px-5 sm:px-8 md:px-12">
                <div className="text-center mb-10 md:mb-12">
                    <div className="text-[10px] md:text-xs font-bold tracking-[0.28em] text-[hsl(var(--muted-foreground))] uppercase">
                        {t(language, "pricing_eyebrow")}
                    </div>
                    <h2 className="mt-3 font-display font-extrabold text-[hsl(var(--primary))] text-3xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05]">
                        {t(language, "pricing_title")}
                    </h2>
                    <p className="mt-4 max-w-2xl mx-auto text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
                        {t(language, "pricing_sub")}
                    </p>
                </div>

                {/* Monthly / Yearly toggle */}
                <div className="mb-10 md:mb-12 flex items-center justify-center gap-3">
                    <div
                        className="relative inline-flex items-center rounded-full bg-[hsl(var(--secondary))] p-1 border border-[hsl(var(--border))]"
                        data-testid="pricing-billing-toggle"
                    >
                        <button
                            onClick={() => setYearly(false)}
                            data-testid="pricing-toggle-monthly"
                            className={`relative z-10 px-5 py-2 rounded-full text-sm font-bold transition-colors ${
                                !yearly ? "text-[hsl(var(--background))]" : "text-[hsl(var(--muted-foreground))]"
                            }`}
                        >
                            {t(language, "pricing_monthly")}
                        </button>
                        <button
                            onClick={() => setYearly(true)}
                            data-testid="pricing-toggle-yearly"
                            className={`relative z-10 px-5 py-2 rounded-full text-sm font-bold transition-colors flex items-center gap-2 ${
                                yearly ? "text-[hsl(var(--background))]" : "text-[hsl(var(--muted-foreground))]"
                            }`}
                        >
                            {t(language, "pricing_yearly")}
                        </button>
                        {/* Sliding thumb */}
                        <motion.span
                            aria-hidden
                            layout
                            className="absolute top-1 bottom-1 rounded-full bg-[hsl(var(--foreground))]"
                            initial={false}
                            animate={{
                                left: yearly ? "50%" : "4px",
                                right: yearly ? "4px" : "50%",
                            }}
                            transition={{ type: "tween", duration: 0.25, ease: [0.2, 0.9, 0.3, 1] }}
                        />
                    </div>
                    {yearly && (
                        <motion.span
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.2 }}
                            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold tracking-[0.14em] uppercase text-[#0A0F1F]"
                            style={{ background: "linear-gradient(120deg, #0AFFB0 0%, #22D3EE 100%)" }}
                            data-testid="pricing-yearly-tag"
                        >
                            {t(language, "pricing_yearly_tag")}
                        </motion.span>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                    {TIERS.map((tier, i) => {
                        const Icon = tier.icon;
                        const featured = tier.featured;
                        const priceKey = yearly ? tier.priceYearlyKey : tier.priceKey;
                        return (
                            <motion.div
                                key={tier.id}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.5, delay: i * 0.08, ease: "easeOut" }}
                                className={`relative rounded-3xl p-6 md:p-8 flex flex-col ${
                                    featured
                                        ? "border-none text-white shadow-2xl"
                                        : "border border-[hsl(var(--border))] bg-[hsl(var(--background))]"
                                }`}
                                style={featured ? {
                                    background: "linear-gradient(135deg, #0A0F1F 0%, #14173A 45%, #2A1454 100%)",
                                    boxShadow: "0 20px 60px -16px rgba(179,108,255,0.4), 0 8px 24px -8px rgba(10,255,176,0.3)",
                                } : undefined}
                                data-testid={`pricing-tier-${tier.id}`}
                            >
                                {featured && (
                                    <>
                                        <div
                                            className="absolute inset-0 pointer-events-none opacity-70 rounded-3xl"
                                            style={{
                                                background: "radial-gradient(circle at 15% 15%, rgba(10,255,176,0.35) 0%, transparent 55%), radial-gradient(circle at 85% 85%, rgba(255,62,165,0.28) 0%, transparent 55%)",
                                            }}
                                        />
                                        <div className="relative -mt-11 mb-4 mx-auto inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0A0F1F]"
                                            style={{ background: "linear-gradient(120deg, #0AFFB0 0%, #B36CFF 100%)" }}
                                        >
                                            {t(language, "pricing_most_popular")}
                                        </div>
                                    </>
                                )}

                                <div className="relative flex items-center gap-3 mb-4">
                                    <div className={`w-11 h-11 rounded-2xl grid place-items-center ${featured ? "bg-white/10" : "bg-[hsl(var(--secondary))] text-[hsl(var(--primary))]"}`}>
                                        <Icon className={`w-5 h-5 ${featured ? "text-[#0AFFB0]" : ""}`} />
                                    </div>
                                    <div className={`text-[11px] font-bold tracking-[0.24em] uppercase ${featured ? "text-white/80" : "text-[hsl(var(--muted-foreground))]"}`}>
                                        {t(language, `${tier.key}_label`)}
                                    </div>
                                </div>

                                <div className="relative">
                                    <h3 className={`font-display font-extrabold text-2xl md:text-3xl tracking-tight ${featured ? "text-white" : "text-[hsl(var(--foreground))]"}`}>
                                        {t(language, `${tier.key}_name`)}
                                    </h3>
                                    <motion.div
                                        key={priceKey}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className={`mt-3 font-display font-black text-4xl md:text-5xl tracking-tight ${featured ? "text-white" : "text-[hsl(var(--foreground))]"}`}
                                    >
                                        {t(language, priceKey)}
                                    </motion.div>
                                    {yearly && tier.tierIndex > 0 && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 0.25, delay: 0.05 }}
                                            className={`mt-1 text-xs ${featured ? "text-white/70" : "text-[hsl(var(--muted-foreground))]"}`}
                                        >
                                            {t(language, `${tier.key}_price_yearly_note`)}
                                        </motion.div>
                                    )}
                                    <p className={`mt-1 text-sm ${featured ? "text-white/80" : "text-[hsl(var(--muted-foreground))]"}`}>
                                        {t(language, `${tier.key}_desc`)}
                                    </p>
                                </div>

                                {/* Full feature list — every tier shows every feature, but lower tiers show locked rows */}
                                <ul className="relative mt-6 space-y-2.5 flex-1">
                                    {ALL_FEATURES.map(({ k, tier: featureTier }) => {
                                        const locked = featureTier > tier.tierIndex;
                                        const included = !locked;
                                        return (
                                            <li
                                                key={k}
                                                className={`flex items-start gap-2.5 transition-opacity ${locked ? "opacity-45" : ""}`}
                                                data-testid={`pricing-feature-${k}${locked ? "-locked" : ""}`}
                                            >
                                                <span
                                                    className="mt-0.5 shrink-0 w-5 h-5 rounded-full grid place-items-center"
                                                    style={
                                                        included
                                                            ? { background: featured ? "rgba(10,255,176,0.2)" : "hsl(var(--primary) / 0.15)", color: featured ? "#0AFFB0" : "hsl(var(--primary))" }
                                                            : { background: featured ? "rgba(255,255,255,0.08)" : "hsl(var(--muted))", color: featured ? "rgba(255,255,255,0.55)" : "hsl(var(--muted-foreground))" }
                                                    }
                                                >
                                                    {included ? <Check className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                                                </span>
                                                <span
                                                    className={`text-sm leading-relaxed ${featured ? (included ? "text-white/90" : "text-white/55 line-through") : (included ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--muted-foreground))] line-through")}`}
                                                    title={locked ? t(language, "pricing_locked_hint") : undefined}
                                                >
                                                    {t(language, k)}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>

                                <a
                                    href="#hero-cta"
                                    onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                    className={`relative mt-8 inline-flex items-center justify-center h-12 px-5 rounded-full font-bold text-sm transition-transform hover:-translate-y-0.5 ${
                                        featured ? "sunset-cta text-white" : "bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:opacity-90"
                                    }`}
                                    data-testid={`pricing-tier-cta-${tier.id}`}
                                >
                                    {t(language, `${tier.key}_cta`)}
                                </a>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default PricingSection;
